import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { EmptyState, ErrorMessage, PageLoader, RoleBadge, Spinner } from '@/components/ui'
import { api } from '@/lib/api'
import { formatDate } from '@/lib/format'
import {
  ROLE_DESCRIPTIONS,
  ROLE_TITLES,
  type InvitationCreatedResponse,
  type InvitationListResponse,
  type MemberListResponse,
  type Role,
} from '@/lib/types'
import { useActiveWorkspace, useAuthStore, useHasPermission } from '@/stores/auth'

const ASSIGNABLE_ROLES: Role[] = ['admin', 'editor', 'viewer', 'accountant']

export function MembersPage() {
  const workspace = useActiveWorkspace()
  const currentUser = useAuthStore((state) => state.user)
  const canManage = useHasPermission('member:manage')
  const queryClient = useQueryClient()

  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<Role>('viewer')
  const [lastInvite, setLastInvite] = useState<InvitationCreatedResponse | null>(null)

  const workspaceId = workspace?.workspace_id

  const members = useQuery({
    queryKey: ['members', workspaceId],
    queryFn: () => api.get<MemberListResponse>(`/workspaces/${workspaceId}/members`),
    enabled: Boolean(workspaceId),
  })

  const invitations = useQuery({
    queryKey: ['invitations', workspaceId],
    queryFn: () => api.get<InvitationListResponse>(`/workspaces/${workspaceId}/invitations`),
    enabled: Boolean(workspaceId) && canManage,
  })

  const invite = useMutation({
    mutationFn: () =>
      api.post<InvitationCreatedResponse>(`/workspaces/${workspaceId}/invitations`, {
        email: inviteEmail,
        role: inviteRole,
      }),
    onSuccess: (data) => {
      setLastInvite(data)
      setInviteEmail('')
      void queryClient.invalidateQueries({ queryKey: ['invitations', workspaceId] })
    },
  })

  const changeRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: Role }) =>
      api.patch(`/workspaces/${workspaceId}/members/${userId}`, { role }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['members', workspaceId] })
    },
  })

  const removeMember = useMutation({
    mutationFn: (userId: string) => api.delete(`/workspaces/${workspaceId}/members/${userId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['members', workspaceId] })
    },
  })

  const revokeInvitation = useMutation({
    mutationFn: (invitationId: string) =>
      api.delete(`/workspaces/${workspaceId}/invitations/${invitationId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invitations', workspaceId] })
    },
  })

  if (!workspace) return <PageLoader />

  return (
    <div className="space-y-6">
      {canManage && (
        <section className="card p-6">
          <h2 className="text-base font-semibold text-slate-900">Пригласить участника</h2>
          <p className="mt-1 text-sm text-slate-500">{ROLE_DESCRIPTIONS[inviteRole]}</p>

          <form
            className="mt-4 flex flex-col gap-3 sm:flex-row"
            onSubmit={(event) => {
              event.preventDefault()
              invite.mutate()
            }}
          >
            <input
              type="email"
              required
              className="input sm:flex-1"
              placeholder="colleague@company.ru"
              value={inviteEmail}
              onChange={(event) => setInviteEmail(event.target.value)}
            />
            <select
              className="input sm:w-52"
              value={inviteRole}
              onChange={(event) => setInviteRole(event.target.value as Role)}
            >
              {ASSIGNABLE_ROLES.map((role) => (
                <option key={role} value={role}>
                  {ROLE_TITLES[role]}
                </option>
              ))}
            </select>
            <button type="submit" className="btn-primary sm:w-auto" disabled={invite.isPending}>
              {invite.isPending && <Spinner className="h-4 w-4" />}
              Пригласить
            </button>
          </form>

          <ErrorMessage error={invite.error} />

          {lastInvite && (
            <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm">
              <p className="font-medium text-emerald-900">
                Приглашение создано для {lastInvite.invitation.email}
              </p>
              {/* Письмо может не уйти: локально почта в MailHog, в production
                  провайдер может быть недоступен. Ссылку всегда показываем,
                  чтобы приглашение можно было передать вручную. */}
              <p className="mt-1 text-emerald-800">
                {lastInvite.email_sent
                  ? 'Письмо отправлено.'
                  : 'Письмо отправить не удалось — передайте ссылку вручную:'}
              </p>
              <code className="mt-2 block break-all rounded bg-white p-2 font-mono text-xs text-slate-700">
                {lastInvite.accept_url}
              </code>
            </div>
          )}
        </section>
      )}

      <section className="card overflow-hidden">
        <header className="border-b border-slate-100 px-6 py-4">
          <h2 className="text-base font-semibold text-slate-900">
            Участники{members.data ? ` · ${members.data.total}` : ''}
          </h2>
        </header>

        {members.isLoading && <PageLoader />}
        <div className="px-6">
          <ErrorMessage error={members.error} />
        </div>

        {members.data && (
          <ul className="divide-y divide-slate-100">
            {members.data.items.map((member) => {
              const isSelf = member.user_id === currentUser?.id
              const isOwner = member.role === 'owner'
              const canEdit = canManage && !isSelf && !isOwner

              return (
                <li
                  key={member.membership_id}
                  className="flex flex-col gap-3 px-6 py-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-900">
                      {member.full_name || member.email}
                      {isSelf && <span className="ml-2 text-xs text-slate-400">это вы</span>}
                    </p>
                    <p className="truncate text-xs text-slate-500">
                      {member.email} · с {formatDate(member.joined_at)}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    {canEdit ? (
                      <select
                        className="input w-40 py-1.5 text-xs"
                        value={member.role}
                        disabled={changeRole.isPending}
                        onChange={(event) =>
                          changeRole.mutate({
                            userId: member.user_id,
                            role: event.target.value as Role,
                          })
                        }
                      >
                        {ASSIGNABLE_ROLES.map((role) => (
                          <option key={role} value={role}>
                            {ROLE_TITLES[role]}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <RoleBadge role={member.role} />
                    )}

                    {canEdit && (
                      <button
                        type="button"
                        className="btn-danger py-1.5 text-xs"
                        disabled={removeMember.isPending}
                        onClick={() => removeMember.mutate(member.user_id)}
                      >
                        Удалить
                      </button>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        )}

        <div className="px-6 pb-4">
          <ErrorMessage error={changeRole.error ?? removeMember.error} />
        </div>
      </section>

      {canManage && (
        <section className="card overflow-hidden">
          <header className="border-b border-slate-100 px-6 py-4">
            <h2 className="text-base font-semibold text-slate-900">Ожидают принятия</h2>
          </header>

          {invitations.data?.items.length === 0 && (
            <div className="p-6">
              <EmptyState
                title="Активных приглашений нет"
                description="Приглашённые участники появятся здесь до того, как примут приглашение."
              />
            </div>
          )}

          {invitations.data && invitations.data.items.length > 0 && (
            <ul className="divide-y divide-slate-100">
              {invitations.data.items.map((invitation) => (
                <li
                  key={invitation.id}
                  className="flex items-center justify-between gap-3 px-6 py-4"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm text-slate-900">{invitation.email}</p>
                    <p className="text-xs text-slate-500">
                      действует до {formatDate(invitation.expires_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <RoleBadge role={invitation.role} />
                    <button
                      type="button"
                      className="btn-ghost py-1.5 text-xs"
                      disabled={revokeInvitation.isPending}
                      onClick={() => revokeInvitation.mutate(invitation.id)}
                    >
                      Отозвать
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="px-6 pb-4">
            <ErrorMessage error={invitations.error ?? revokeInvitation.error} />
          </div>
        </section>
      )}
    </div>
  )
}
